import { Card, Colors, Heading, Icon, Text } from '@amelio/ui-library';
import { useEffect, useState } from 'react';

import { CompetencyTranslationRow } from './CompetencyTranslationRow';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';

import { CompetencyTranslationFieldKind } from '@/constants/competencies/CompetencyTranslationFieldKind';
import { useAmelioTranslations } from '@/hooks';
import { getLanguageDisplayName, getTextByLanguage } from '@/utils/localization';

interface ProficiencyLevelsTranslationAccordionProps {
    levels: ProficiencyLevel[];
    sourceLanguage: string;
    targetLanguage: string;
    onChange: (index: number, value: string) => void;
}

export const ProficiencyLevelsTranslationAccordion = ({
    levels,
    sourceLanguage,
    targetLanguage,
    onChange,
}: ProficiencyLevelsTranslationAccordionProps) => {
    const { t } = useAmelioTranslations();

    const total = levels.length;
    const translated = levels.filter(
        (level) => (level.label[targetLanguage] ?? '').trim().length > 0,
    ).length;
    const isComplete = total > 0 && translated === total;

    const [isOpen, setIsOpen] = useState<boolean>(!isComplete);

    useEffect(() => {
        if (!isComplete) {
            setIsOpen(true);
        }
    }, [isComplete]);

    return (
        <Card
            borderRadius={'2xl'}
            padding={'none'}
            className={'overflow-hidden'}
        >
            <button
                type={'button'}
                onClick={() => setIsOpen((open) => !open)}
                className={
                    'flex w-full flex-col flex-wrap items-center justify-between border border-gray-100 bg-purple-50 px-6 py-4 text-left max-md:gap-3 sm:flex-row'
                }
                aria-expanded={isOpen}
                data-testid={'translation-levels-accordion'}
            >
                <Heading
                    level={5}
                    weight={'semibold'}
                    className={'text-md'}
                >
                    {t('competenciesLevels.title')}
                </Heading>

                <div className={'flex items-center gap-3'}>
                    <div className={'flex items-center gap-3'}>
                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'text-caption whitespace-nowrap'}
                            data-testid={'translation-levels-count'}
                        >
                            {t('competenciesTranslations.fieldsTranslated', {
                                translated,
                                total,
                            })}
                        </Text>

                        <Icon
                            name={isComplete ? 'essential-tick-circle' : 'essential-danger'}
                            color={isComplete ? Colors.SUCCESS : Colors.WARNING}
                        />

                        <Icon
                            name={'arrow-arrow-down'}
                            className={`transition-transform ${isOpen ? '' : '-rotate-90'}`}
                        />
                    </div>
                </div>
            </button>

            {isOpen && (
                <div className={'bg-white/80 px-6 py-4'}>
                    <div className={'border-base-100 flex gap-6 pb-2'}>
                        <Text
                            weight={'medium'}
                            color={Colors.BASE_500}
                            className={'flex-1 text-[12px] uppercase'}
                        >
                            {getLanguageDisplayName(sourceLanguage)}
                        </Text>

                        <Text
                            weight={'medium'}
                            color={Colors.BASE_500}
                            className={'flex-1 text-[12px] uppercase'}
                        >
                            {sourceLanguage !== targetLanguage
                                ? getLanguageDisplayName(targetLanguage)
                                : ''}
                        </Text>
                    </div>

                    {levels.map((level, index) => (
                        <CompetencyTranslationRow
                            key={level.id ?? index}
                            variant={CompetencyTranslationFieldKind.COMPETENCY_NAME}
                            sourceText={getTextByLanguage(level.label, sourceLanguage)}
                            targetValue={level.label[targetLanguage] ?? ''}
                            onTargetChange={(value) => onChange(index, value)}
                        />
                    ))}
                </div>
            )}
        </Card>
    );
};
